import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { registerUiTranslations } from '@repo/ui/i18n';

import itCommon from './locales/it/common.json';
import itAuth from './locales/it/auth.json';
import itLayout from './locales/it/layout.json';
import itDashboard from './locales/it/dashboard.json';
import itTenants from './locales/it/tenants.json';
import itAppointee from './locales/it/appointee.json';
import itReferrers from './locales/it/referrers.json';
import itContracts from './locales/it/contracts.json';
import itAdmins from './locales/it/admins.json';
import itAdvisors from './locales/it/advisors.json';
import itProductReleases from './locales/it/product-releases.json';
import itUsage from './locales/it/usage.json';
import itInfrastructureCosts from './locales/it/infrastructure-costs.json';
import itSystemMonitor from './locales/it/system-monitor.json';
import itMcpClients from './locales/it/mcp-clients.json';
import itErrorLogs from './locales/it/error-logs.json';
import itAcubeJobs from './locales/it/acube-jobs.json';
import itAcubePayments from './locales/it/acube-payments.json';
import itAdminNotifications from './locales/it/admin-notifications.json';
import itSupportTickets from './locales/it/support-tickets.json';
import itForeignVendors from './locales/it/foreign-vendors.json';

import enCommon from './locales/en/common.json';
import enAuth from './locales/en/auth.json';
import enLayout from './locales/en/layout.json';
import enDashboard from './locales/en/dashboard.json';
import enTenants from './locales/en/tenants.json';
import enAppointee from './locales/en/appointee.json';
import enReferrers from './locales/en/referrers.json';
import enContracts from './locales/en/contracts.json';
import enAdmins from './locales/en/admins.json';
import enAdvisors from './locales/en/advisors.json';
import enProductReleases from './locales/en/product-releases.json';
import enUsage from './locales/en/usage.json';
import enInfrastructureCosts from './locales/en/infrastructure-costs.json';
import enSystemMonitor from './locales/en/system-monitor.json';
import enMcpClients from './locales/en/mcp-clients.json';
import enErrorLogs from './locales/en/error-logs.json';
import enAcubeJobs from './locales/en/acube-jobs.json';
import enAcubePayments from './locales/en/acube-payments.json';
import enAdminNotifications from './locales/en/admin-notifications.json';
import enSupportTickets from './locales/en/support-tickets.json';
import enForeignVendors from './locales/en/foreign-vendors.json';

// Modulo di traduzione → chiavi top-level che contiene. Ogni chiave vive in
// un solo modulo: due moduli con la stessa chiave si sovrascriverebbero in
// silenzio nello spread (`translation-modules.test.ts` lo impedisce).
export const TRANSLATION_MODULES = {
  common: [
    'commons',
    'errors',
    'toast',
    'validation',
    'months',
    'versionNotice',
  ],
  auth: ['auth', 'invitation'],
  layout: ['sidebar', 'navbar', 'command_palette'],
  dashboard: ['dashboard'],
  tenants: [
    'tenants',
    'tax_box',
    'preferences',
    'fabrick_migration',
    'acube_rollback',
  ],
  appointee: ['appointee'],
  referrers: ['referrers', 'referrer_panel', 'commissions'],
  contracts: ['contracts'],
  admins: ['admins'],
  advisors: ['advisors'],
  productReleases: ['product_releases'],
  usage: ['aiUsage', 'serviceUsage'],
  infrastructureCosts: ['infrastructureCosts'],
  systemMonitor: ['systemMonitor'],
  mcpClients: ['mcpClients'],
  errorLogs: ['errorLogs'],
  acubeJobs: ['acubeJobs'],
  acubePayments: ['acubePayments'],
  adminNotifications: ['adminNotifications'],
  supportTickets: ['support_tickets'],
  foreignVendors: ['foreignVendors'],
} as const;

export type TranslationModule = keyof typeof TRANSLATION_MODULES;

const itTranslation = {
  ...itCommon,
  ...itAuth,
  ...itLayout,
  ...itDashboard,
  ...itTenants,
  ...itAppointee,
  ...itReferrers,
  ...itContracts,
  ...itAdmins,
  ...itAdvisors,
  ...itProductReleases,
  ...itUsage,
  ...itInfrastructureCosts,
  ...itSystemMonitor,
  ...itMcpClients,
  ...itErrorLogs,
  ...itAcubeJobs,
  ...itAcubePayments,
  ...itAdminNotifications,
  ...itSupportTickets,
  ...itForeignVendors,
};

const enTranslation = {
  ...enCommon,
  ...enAuth,
  ...enLayout,
  ...enDashboard,
  ...enTenants,
  ...enAppointee,
  ...enReferrers,
  ...enContracts,
  ...enAdmins,
  ...enAdvisors,
  ...enProductReleases,
  ...enUsage,
  ...enInfrastructureCosts,
  ...enSystemMonitor,
  ...enMcpClients,
  ...enErrorLogs,
  ...enAcubeJobs,
  ...enAcubePayments,
  ...enAdminNotifications,
  ...enSupportTickets,
  ...enForeignVendors,
};

const resources = {
  it: {
    translation: itTranslation,
  },
  en: {
    translation: enTranslation,
  },
};

i18n.use(initReactI18next).init({
  lng: 'it',
  resources,
  fallbackLng: 'it',
  returnNull: false,
  // Niente banner promozionale di i18next in console (anche nei test).
  showSupportNotice: false,
  interpolation: {
    // React fa già l'escape in render: quello di i18next trasformava i "/"
    // delle date interpolate in `&#x2F;` a video.
    escapeValue: false,
  },
});

// Testi dei componenti di @repo/ui, nel loro namespace `ui`.
registerUiTranslations(i18n);

export default i18n;
